import { initializeFaro, getWebInstrumentations, Faro } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

export interface FaroConfig {
  url: string;
  appName: string;
  appVersion: string;
  environment: string;
  enabled?: boolean;
}

/**
 * Get Faro-specific configuration from environment variables
 */
const getFaroConfig = (): FaroConfig => {
  const url = process.env.NEXT_PUBLIC_FARO_URL || '';

  return {
    url,
    appName: process.env.NEXT_PUBLIC_FARO_APP_NAME || 'tg-website',
    appVersion: process.env.NEXT_PUBLIC_FARO_APP_VERSION || '1.0.0',
    environment: process.env.NEXT_PUBLIC_FARO_ENVIRONMENT || 'production',
    enabled: Boolean(url),
  };
};

const getRuntimeFaroConfig = async (): Promise<Partial<FaroConfig> | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const response = await fetch('/api/observability/faro-config', {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      console.warn('[FaroService] Runtime Faro config request failed', response.status);
      return { enabled: false };
    }

    return await response.json();
  } catch (error) {
    console.warn('[FaroService] Failed to load runtime Faro config', error);
    return { enabled: false };
  }
};

class FaroService {
  private faro: Faro | null = null;
  private isInitialized = false;
  private config: FaroConfig | null = null;

  /**
   * Initialize Faro SDK with React optimized configuration
   * Based on Grafana Data Router V6 Settings configuration
   */
  initialize = async (config: Partial<FaroConfig> = {}): Promise<boolean> => {
    // Check if Faro is already initialized by checking if we already have an instance
    if (this.faro) {
      console.log('[FaroService] initialize() Using existing Faro instance');
      this.isInitialized = true;
      return true;
    }

    // Skip initialization if already initialized
    if (this.isInitialized && this.faro) {
      console.log('[FaroService] initialize() Faro already initialized, skipping');
      return true;
    }

    // Suppress Faro transport errors from showing in Next.js dev overlay
    this._suppressFaroConsoleErrors();
    
    try {
      // Prefer same-origin runtime config so one web image can safely run in
      // beta, dev, or local without baking collector/base URL choices into JS.
      const defaultConfig = getFaroConfig();
      const runtimeConfig = await getRuntimeFaroConfig();
      this.config = {
        url: config.url || runtimeConfig?.url || defaultConfig.url,
        appName: config.appName || runtimeConfig?.appName || defaultConfig.appName,
        appVersion: config.appVersion || runtimeConfig?.appVersion || defaultConfig.appVersion,
        environment: config.environment || runtimeConfig?.environment || defaultConfig.environment,
        enabled: config.enabled ?? runtimeConfig?.enabled ?? defaultConfig.enabled,
      };

      // Debug: Log the configuration
      // console.log('[FaroService] initialize() Configuration loaded:', this.config);

      // Skip initialization if required config is missing
      if (!this.config.enabled || !this.config.url) {
        console.warn('[FaroService] initialize() Faro URL not configured, skipping initialization');
        console.warn('[FaroService] initialize() Expected FARO_COLLECTOR_URL runtime environment variable');
        return false;
      }

      // console.log('[FaroService] initialize() Initializing Faro SDK for React/Next.js with URL:', this.config.url);

      // Initialize Faro with React optimized instrumentation
      this.faro = initializeFaro({
        url: this.config.url,
        app: {
          name: this.config.appName,
          version: this.config.appVersion,
          environment: this.config.environment,
        },
        instrumentations: [
          // Mandatory: Default web instrumentations for Core Web Vitals, errors, etc.
          ...getWebInstrumentations(),

          // Distributed tracing for end-to-end visibility
          new TracingInstrumentation(),

          // Note: React Integration will be handled separately via ReactIntegration
          // This allows for better component-level tracking and error boundaries
        ],
        
        // Batching tuned to keep payloads under the collector proxy's body cap.
        // During recording storms (STT + companions + traces) a 100-item batch
        // blew past the proxy limit and 413'd, dropping all telemetry (#1972).
        // Smaller batches flushed more often stay well under the cap.
        batching: {
          enabled: true,
          sendTimeout: 3000, // Flush every 3 seconds
          itemLimit: 40,     // Or when 40 events collected
        },
        
        // Session tracking configuration
        sessionTracking: {
          enabled: true,
          persistent: true, // Persist session across page loads
        },
        
        // Ignore internal Next.js routes and assets
        ignoreUrls: [
          // Next.js internal routes
          /\/_next\/static/,
          /\/_next\/image/,
          /\/api\/health/,
          /\/favicon\.ico/,
          /\/robots\.txt/,
          /\/sitemap\.xml/,
          // Development hot reload
          /\/__nextjs_original-stack-frame/,
          /\/_next\/webpack-hmr/,
        ],
        
        // Silent error handling - log to console only, no popups
        beforeSend: (item) => {
          // Intercept transport errors and log them silently
          if ((item as any).type === 'error' && (item as any).payload?.message?.includes('Failed sending payload')) {
            console.log('[Faro] Transport error (silenced):', (item as any).payload.message);
            return null; // Drop the error to prevent popup
          }
          return item;
        },
      });

      this.isInitialized = true;
      // console.log('[FaroService] initialize() Faro SDK initialized successfully for React/Next.js');
      
      // Set up Next.js specific navigation tracking
      this._setupNextJsNavigationTracking();
      
      return true;
    } catch (error) {
      // Handle specific Faro duplicate registration error
      if (error instanceof Error && error.message.includes('Faro is already registered')) {
        console.log('[FaroService] initialize() Faro already registered globally, skipping initialization');
        this.isInitialized = true;
        return true;
      }
      console.error('[FaroService] initialize() Failed to initialize Faro SDK:', error);
      return false;
    }
  };

  /**
   * Suppress Faro transport errors from appearing in Next.js dev overlay
   */
  private _suppressFaroConsoleErrors = (): void => {
    if (typeof window === 'undefined') return;

    // Store original console.error
    const originalConsoleError = console.error;
    
    // Override console.error to filter Faro transport errors
    console.error = (...args: any[]) => {
      const errorMessage = args.join(' ');
      
      // Check if this is a Faro transport error
      if (errorMessage.includes('@grafana/faro-web-sdk:transport-fetch') && 
          errorMessage.includes('Failed sending payload to the receiver')) {
        // Log it silently without triggering Next.js dev overlay
        console.log('[Faro] Transport error (suppressed from overlay):', ...args);
        return;
      }
      
      // For all other errors, use original console.error
      originalConsoleError.apply(console, args);
    };
  };

  /**
   * Set up Next.js navigation tracking (since we don't use React Router)
   */
  private _setupNextJsNavigationTracking = (): void => {
    if (typeof window === 'undefined' || !this.faro) return;

    // Track Next.js page navigation using the History API
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    // Override pushState to track navigation
    window.history.pushState = (data: any, unused: string, url?: string | URL | null) => {
      originalPushState(data, unused, url);
      this.trackEvent('page_navigation', {
        type: 'pushState',
        url: window.location.href,
        pathname: window.location.pathname,
      });
    };

    // Override replaceState to track navigation
    window.history.replaceState = (data: any, unused: string, url?: string | URL | null) => {
      originalReplaceState(data, unused, url);
      this.trackEvent('page_navigation', {
        type: 'replaceState',
        url: window.location.href,
        pathname: window.location.pathname,
      });
    };

    // Track back/forward navigation
    window.addEventListener('popstate', () => {
      this.trackEvent('page_navigation', {
        type: 'popstate',
        url: window.location.href,
        pathname: window.location.pathname,
      });
    });

    // Track initial page load
    this.trackEvent('page_load', {
      url: window.location.href,
      pathname: window.location.pathname,
      referrer: document.referrer,
    });
  };

  /**
   * Track custom business event
   */
  trackEvent = (name: string, attributes: Record<string, any> = {}): void => {
    if (!this.isInitialized || !this.faro) {
      console.warn(`[FaroService] trackEvent(${name}) Faro not initialized`);
      return;
    }

    try {
      this.faro.api.pushEvent(name, {
        ...attributes,
        // Add consistent context
        timestamp: new Date().toISOString(),
        ...(this.config?.environment && { environment: this.config.environment }),
        ...(this.config?.appVersion && { appVersion: this.config.appVersion }),
      });
      // console.log(`[FaroService] trackEvent(${name}) Event tracked:`, attributes);
    } catch (error) {
      console.error(`[FaroService] trackEvent(${name}) Failed to track event:`, error);
    }
  };

  /**
   * Track custom measurement with enhanced context
   */
  trackMeasurement = (name: string, value: number, attributes: Record<string, any> = {}): void => {
    if (!this.isInitialized || !this.faro) {
      console.warn(`[FaroService] trackMeasurement(${name}) Faro not initialized`);
      return;
    }

    try {
      const measurementData = {
        type: 'custom',
        values: { [name]: value },
      };
      
      const contextData = {
        ...attributes,
        // Add consistent context
        timestamp: new Date().toISOString(),
        ...(this.config?.environment && { environment: this.config.environment }),
        ...(this.config?.appVersion && { appVersion: this.config.appVersion }),
      };

      // console.log(`[FaroService] trackMeasurement(${name}) Detailed payload:`, {
      //   measurementData,
      //   contextData,
      //   faroUrl: this.config?.url,
      //   appName: this.config?.appName
      // });

      this.faro.api.pushMeasurement(measurementData, {
        context: contextData,
      });
      // console.log(`[FaroService] trackMeasurement(${name}) Measurement tracked:`, { value, attributes });
    } catch (error) {
      console.error(`[FaroService] trackMeasurement(${name}) Failed to track measurement:`, error);
    }
  };

  /**
   * Set user context for session tracking
   */
  setUser = (userId: string, attributes: Record<string, any> = {}): void => {
    if (!this.isInitialized || !this.faro) {
      console.warn('[FaroService] setUser() Faro not initialized');
      return;
    }

    try {
      this.faro.api.setUser({
        id: userId,
        ...attributes,
      });
      console.log('[FaroService] setUser() User context set:', { userId, attributes });
    } catch (error) {
      console.error('[FaroService] setUser() Failed to set user context:', error);
    }
  };

  /**
   * Track Next.js specific events
   */
  trackNextJsEvent = (eventType: 'page_view' | 'route_change' | 'api_call', data: Record<string, any>): void => {
    this.trackEvent(`nextjs_${eventType}`, {
      ...data,
      framework: 'nextjs',
      router: 'app_router', // or 'pages_router' based on your setup
    });
  };

  /**
   * Track Telegamez game-specific events
   */
  trackGameEvent = (eventType: string, gameData: Record<string, any>): void => {
    this.trackEvent(`game_${eventType}`, {
      ...gameData,
      category: 'game_analytics',
    });
  };

  /**
   * Track STT performance events
   */
  trackSTTEvent = (eventType: string, performanceData: Record<string, any>): void => {
    this.trackEvent(`stt_${eventType}`, {
      ...performanceData,
      category: 'stt_performance',
    });
  };

  /**
   * Add session attributes via event context
   */
  setSessionAttribute = (key: string, value: string): void => {
    if (!this.isInitialized || !this.faro) {
      console.warn(`[FaroService] setSessionAttribute(${key}) Faro not initialized`);
      return;
    }

    try {
      // Use pushEvent with session context instead of direct session attribute
      this.faro.api.pushEvent('session_attribute_update', {
        attribute_key: key,
        attribute_value: value,
      });
      console.log(`[FaroService] setSessionAttribute(${key}) Session attribute set:`, value);
    } catch (error) {
      console.error(`[FaroService] setSessionAttribute(${key}) Failed to set session attribute:`, error);
    }
  };

  /**
   * Get current Faro instance (for advanced usage)
   */
  getFaro = (): Faro | null => {
    return this.faro;
  };

  /**
   * Check if Faro is initialized
   */
  isReady = (): boolean => {
    return this.isInitialized && this.faro !== null;
  };

  /**
   * Get current configuration
   */
  getConfig = (): FaroConfig | null => {
    return this.config;
  };
}

// Export singleton instance
export const faroService = new FaroService();
export default faroService;
